import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectModalData } from '../../../../redux/selectors/modal';
import { closeModal } from '../../../../redux/slices/modal';
import { useUpdateAnalysisInfo } from '../../../../utils/hooks';
import { PrimaryButton, SecondaryButton } from '../../../Button';
import { FieldLabel, InputField, TextAreaField } from '../../../Form';
import KubitModal from '../../../KubitModal';
import {
  CHART_NAME_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
} from '../../../../config/constants';
import toastMessage from '../../../../utils/toastMessage';
import { useChartContext } from '../../ChartContext';
import { assertCondition } from '../../../../utils/types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 10px 0;
`;

const FooterActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

interface IProps {
  analysisId: string;
}

const RenameReportModal = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const data = useAppSelector(selectModalData);
  const { display, description } = data;

  const chartContext = useChartContext();
  assertCondition(chartContext.__typename === 'BaseChartContext');
  const {
    actions: { updateDisplay, updateDescription },
  } = chartContext;

  const { updateAnalysisInfo } = useUpdateAnalysisInfo({
    analysisId,
    currentName: display,
    currentDescription: description,
  });

  const { control, handleSubmit } = useForm({
    defaultValues: {
      display,
      description,
    },
  });

  const onCloseModal = () => {
    dispatch(closeModal());
  };

  const onSubmit = async (formData: {
    display: string;
    description: string;
  }) => {
    await updateAnalysisInfo(formData.display, formData.description);
    updateDisplay(formData.display);
    updateDescription(formData.description);

    toastMessage({
      message: 'Report renamed successfully',
      type: 'info',
    });

    onCloseModal();
  };

  return (
    <KubitModal open onClose={onCloseModal} header="Rename Report">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container>
          <FieldLabel>Name</FieldLabel>
          <Controller
            name="display"
            control={control}
            render={({ field }) => (
              <InputField {...field} maxLength={CHART_NAME_MAX_LENGTH} />
            )}
          />
          <FieldLabel>Description</FieldLabel>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextAreaField
                {...field}
                minRows={3}
                maxLength={DESCRIPTION_MAX_LENGTH}
              />
            )}
          />
        </Container>
        <Footer>
          <div />
          <FooterActions>
            <SecondaryButton
              type="button"
              onClick={onCloseModal}
              trackContext="reports"
              trackAction="rename - cancel"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              trackContext="reports"
              trackAction="rename - save"
            >
              Save
            </PrimaryButton>
          </FooterActions>
        </Footer>
      </form>
    </KubitModal>
  );
};

export default RenameReportModal;
